import { React } from 'Imports';
import { useState, useEffect, useRef } from 'react';
import { ClickAwayListener, IconButton, Backdrop } from '@mui/material';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { isEncompass } from '$Utilities/integrationPlatform';
import { IDropdownItem } from '../VPDropdown';
import { MultiSelect, SearchField } from '$Components/FilterComponents/Filters';
import DateRangePicker from '$Components/FilterComponents/DateRangePicker/DateRangePicker';
import {
    FilterType,
    FilterConfig,
    MultiselectFilterConfig,
    DateRangeFilterConfig,
    TextFieldFilterConfig,
    IConfigurableFilterBarProps,
} from './types';

const styles = require('$Components/FilterComponents/ConfigurableFilterBar/ConfigurableFilterBar.scss') as {
    filterContainer: string;
    filterContainerGeotab: string;
    filterContainerEncompass: string;
    filterGroupDesktop: string;
    filterGroupMobile: string;
    mobileHeader: string;
    mobileFilterBackdrop: string;
    mobileFilterDrawer: string;
    mobileFilterDrawerGeotab: string;
    mobileFilterDrawerEncompass: string;
    mobileFilterDrawerHeader: string;
    mobileFilterDrawerTitleGeotab: string;
    mobileFilterDrawerTitleEncompass: string;
    mobileFilterDrawerCloseButton: string;
    rightSlotDefault: string;
};

const ConfigurableFilterBar = (props: IConfigurableFilterBarProps) => {
    const [autoMobileView, setAutoMobileView] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const filterGroupRef = useRef<HTMLDivElement>(null);
    const desktopMinWidth = useRef<number | null>(null);

    const isAutoDetect = props.isMobileView === undefined;
    const isMobileView = isAutoDetect ? autoMobileView : !!props.isMobileView;

    // Auto-detect overflow when isMobileView prop is not provided
    useEffect(() => {
        if (!isAutoDetect) return;
        const container = containerRef.current;
        if (!container) return;

        const observer = new ResizeObserver(() => {
            const filterGroup = filterGroupRef.current;
            if (filterGroup) {
                // Desktop: switch to mobile if filters overflow
                if (filterGroup.scrollWidth > filterGroup.clientWidth) {
                    // Cache the actual container width needed: filter content width + everything else (right slot, gaps, padding)
                    const nonFilterWidth = container.clientWidth - filterGroup.clientWidth;
                    desktopMinWidth.current = filterGroup.scrollWidth + nonFilterWidth;
                    setAutoMobileView(true);
                }
            } else if (desktopMinWidth.current !== null) {
                // Mobile: switch back when container is wide enough
                if (container.clientWidth >= desktopMinWidth.current) {
                    setAutoMobileView(false);
                }
            }
        });

        observer.observe(container);
        return () => observer.disconnect();
    }, [isAutoDetect, autoMobileView]);

    // Notify parent when auto-detected mobile view changes
    useEffect(() => {
        if (isAutoDetect) {
            props.onMobileViewChange?.(autoMobileView);
        }
    }, [autoMobileView, isAutoDetect]);

    // Lock body scroll while the mobile filter drawer is open (mirrors MUI Modal/Dialog),
    // so the page scrollbar isn't visible behind the backdrop. Pad for the removed
    // scrollbar width to avoid the page shifting underneath.
    useEffect(() => {
        if (!props.mobileProps?.drawerIsOpen) return;

        const { body, documentElement } = document;
        const scrollBarWidth = window.innerWidth - documentElement.clientWidth;
        const originalOverflow = body.style.overflow;
        const originalPaddingRight = body.style.paddingRight;

        body.style.overflow = 'hidden';
        if (scrollBarWidth > 0) {
            body.style.paddingRight = `${scrollBarWidth}px`;
        }

        return () => {
            body.style.overflow = originalOverflow;
            body.style.paddingRight = originalPaddingRight;
        };
    }, [props.mobileProps?.drawerIsOpen]);

    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [datePickerHasPendingChanges, setDatePickerHasPendingChanges] = useState(false);

    const handleDatePickerStateChange = (isOpen: boolean, hasPendingChanges: boolean) => {
        setDatePickerOpen(isOpen);
        setDatePickerHasPendingChanges(hasPendingChanges);
    };

    const onClickAwayFromDrawer = () => {
        if (datePickerOpen && datePickerHasPendingChanges) {
            return;
        }

        if (props.mobileProps?.onCloseDrawer) {
            props.mobileProps.onCloseDrawer();
        }
    };

    const handleMultiSelectFilterChange = (filterKey: string, selectedItems: IDropdownItem[]) => {
        props.onFilterChange(
            filterKey,
            selectedItems.map((item) => item.value),
        );
    };

    // helper method for rendering filters based on the provided filter config props
    const renderFilter = (filterConfig: FilterConfig) => {
        const isDisabled = props.disabled || filterConfig.disabled;

        switch (filterConfig.type) {
            case FilterType.Multiselect:
                const multiselectConfig = filterConfig as MultiselectFilterConfig;
                return (
                    <MultiSelect
                        key={filterConfig.key}
                        label={multiselectConfig.label}
                        items={multiselectConfig.items || []}
                        disabled={!!isDisabled}
                        applyFilter={(selectedItems: IDropdownItem[]) => handleMultiSelectFilterChange(filterConfig.key, selectedItems)}
                        searchable={multiselectConfig.searchable}
                        toggleable={multiselectConfig.toggleable}
                        toggleText={multiselectConfig.toggleText}
                        toggleStatus={multiselectConfig.toggleStatus}
                        onToggleChange={multiselectConfig.onToggleChange}
                        selectedValues={multiselectConfig.selectedValues}
                        isMobileView={isMobileView}
                        sortItemsByValue={multiselectConfig.sortItemsByValue}
                    />
                );

            case FilterType.DateRange:
                const dateRangeConfig = filterConfig as DateRangeFilterConfig;
                return (
                    <DateRangePicker
                        key={filterConfig.key}
                        startDate={dateRangeConfig.startDate}
                        endDate={dateRangeConfig.endDate}
                        onDateRangeApply={dateRangeConfig.onDateRangeApply}
                        onPickerStateChange={handleDatePickerStateChange}
                        isMobileView={isMobileView}
                        maxDays={dateRangeConfig.maxDays}
                    />
                );

            case FilterType.TextField:
                const textFieldConfig = filterConfig as TextFieldFilterConfig;
                return (
                    <SearchField
                        key={filterConfig.key}
                        label={textFieldConfig.label}
                        value={textFieldConfig.value}
                        onChange={(value: any) => props.onFilterChange(filterConfig.key, value)}
                        onClear={textFieldConfig.onClear}
                        placeholder={textFieldConfig.placeholder}
                        isMobileView={isMobileView}
                    />
                );

            default:
                return null;
        }
    };

    if (isMobileView) {
        return (
            <>
                <div
                    ref={containerRef}
                    className={classNames(
                        styles.filterContainer,
                        isEncompass ? styles.filterContainerEncompass : styles.filterContainerGeotab,
                    )}
                >
                    <div>{props.slotProps?.mobile?.left}</div>
                    <div className={styles.rightSlotDefault}>{props.slotProps?.mobile?.right}</div>
                </div>
                {props.mobileProps?.drawerIsOpen && (
                    <div>
                        <Backdrop className={styles.mobileFilterBackdrop} open={true} />
                        <ClickAwayListener onClickAway={onClickAwayFromDrawer}>
                            <div
                                className={classNames(
                                    styles.mobileFilterDrawer,
                                    isEncompass ? styles.mobileFilterDrawerEncompass : styles.mobileFilterDrawerGeotab,
                                )}
                            >
                                <div className={styles.mobileFilterDrawerHeader}>
                                    <div
                                        className={
                                            isEncompass ? styles.mobileFilterDrawerTitleEncompass : styles.mobileFilterDrawerTitleGeotab
                                        }
                                    >
                                        {props.mobileProps?.drawerTitle ? props.mobileProps.drawerTitle : 'Filters'}
                                    </div>
                                    <IconButton
                                        disableRipple
                                        className={styles.mobileFilterDrawerCloseButton}
                                        onClick={props.mobileProps?.onCloseDrawer}
                                    >
                                        <FontAwesomeIcon
                                            icon={faXmark}
                                            size={'2xs'}
                                            onClick={props.mobileProps?.onCloseDrawer}
                                            color={isEncompass ? '#373E40' : '#8DA4B9'}
                                        />
                                    </IconButton>
                                </div>
                                <div className={styles.filterGroupMobile}>{props.filters.map(renderFilter)}</div>
                            </div>
                        </ClickAwayListener>
                    </div>
                )}
            </>
        );
    }

    // Desktop view (filters left-aligned, ignoring 'left' slot, 'right' slot on the right, (typically action buttons))
    return (
        <div
            ref={containerRef}
            className={classNames(styles.filterContainer, isEncompass ? styles.filterContainerEncompass : styles.filterContainerGeotab)}
        >
            <div ref={filterGroupRef} className={styles.filterGroupDesktop}>
                {props.filters.map(renderFilter)}
            </div>
            <div className={styles.rightSlotDefault}>{props.slotProps?.desktop?.right}</div>
        </div>
    );
};

export default ConfigurableFilterBar;
